import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setBranchId } from "@/lib/api";
import { getMeta, clearSession } from "@/lib/offline";

const AuthCtx = createContext(null);

function hexToHsl(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const n = parseInt(m[1], 16);
  let r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function applyBranding(branding) {
  if (!branding) return;
  const root = document.documentElement.style;
  const p = hexToHsl(branding.primary), s = hexToHsl(branding.secondary), a = hexToHsl(branding.accent);
  if (p) { root.setProperty("--primary", p); root.setProperty("--ring", p); root.setProperty("--chart-1", p); }
  if (s) { root.setProperty("--secondary", s); root.setProperty("--chart-2", s); }
  if (a) { root.setProperty("--gold", a); root.setProperty("--chart-3", a); }
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null); // null = checking, false = signed out
  const [branch, setBranch] = useState(localStorage.getItem("pf_branch") || "");

  const accept = useCallback((data) => {
    const branches = data.branches || [];
    const keep = branches.find((b) => b.id === localStorage.getItem("pf_branch"));
    const b = keep?.id || branches[0]?.id || "";
    setBranchId(b); setBranch(b);
    applyBranding(data.tenant?.branding);
    setSession(data);
  }, []);

  const refresh = useCallback(async () => {
    try { accept((await api.get("/auth/me")).data); } catch (e) {
      const snap = !e.response && (await getMeta("snapshot").catch(() => null));
      if (snap && new Date(snap.session.expires_at) > new Date()) {
        setSession({ offline: true, user: { id: snap.session.user_id, name: snap.session.name, role: snap.session.role }, permissions: ["pos.sell"], branches: [], tenant: null, mfa: {} });
      } else setSession(false);
    }
  }, [accept]);

  useEffect(() => {
    refresh();
    const off = () => setSession(false);
    window.addEventListener("pf:logout", off);
    return () => window.removeEventListener("pf:logout", off);
  }, [refresh]);

  const login = async (identifier, password, portal) => { const r = await api.post("/auth/login", { identifier, password, portal: portal || null }); if (!r.data.mfa_required) accept(r.data); return r.data; };
  const verifyMfa = async (challenge_id, code) => { const r = await api.post("/auth/mfa/verify", { challenge_id, code }); accept(r.data); return r.data; };
  const logout = async () => { try { await api.post("/auth/logout"); } catch { /* ignore */ } await clearSession().catch(() => {}); setSession(false); };
  const switchBranch = (id) => { setBranchId(id); setBranch(id); };
  const can = (perm) => !!session && session.permissions?.includes(perm);

  return <AuthCtx.Provider value={{ session, user: session?.user, tenant: session?.tenant, branches: session?.branches || [], branch,
    login, verifyMfa, logout, refresh, accept, can, switchBranch }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

export const homeFor = (s) => {
  const p = s?.permissions || [];
  if (p.includes("platform")) return "/platform";
  if (p.includes("dashboard")) return "/app";
  if (p.includes("pos.sell")) return "/app/pos";
  return "/app/products";
};
